import {LoadSpacingScenario} from '@hconnect/tuc-processes/fields/loadSpacing/types'
import {useLoadSpacingScenario} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacingScenario'
import {useMemo} from 'react'

import {useRmcCountryFeatures} from '../../shared/order-editor/hooks/useRmcCountryFeatures'

/**
 * Enhanced hook that extends the base load spacing scenario with country-specific features.
 * This hook layers country features on top of the existing configuration-based scenarios
 * while maintaining backward compatibility.
 *
 * For the GB example:
 * - allowTimeOnJobSite: true, allowFlowRate: false, alwaysHasTimeOnJobSite: true
 * - Results in: timeOnJobSite + spacing selector, but NO flowRate selector
 */
export const useEnhancedLoadSpacingScenario = (): LoadSpacingScenario => {
  // Get base scenario from existing logic (respects configuration and isMultipleTruck)
  const baseScenario = useLoadSpacingScenario()
  const countryFeatures = useRmcCountryFeatures()

  return useMemo(() => {
    // If load spacing is completely disabled, return the base scenario
    if (!baseScenario.timeOnJobSite && !baseScenario.loadSpace && !baseScenario.flowRate) {
      return baseScenario
    }

    // Apply country-specific enhancements to the base scenario
    const enhancedScenario: LoadSpacingScenario = {
      // Keep spacing logic from base scenario (usually true for multiple trucks)
      loadSpace: baseScenario.loadSpace,

      // Override flow rate: only allow if both base scenario allows it AND country allows it
      flowRate: baseScenario.flowRate && countryFeatures.allowFlowRate,

      // Enhanced time on job site logic:
      // Show if country allows it AND (always required OR base scenario allows it)
      timeOnJobSite:
        countryFeatures.allowTimeOnJobSite &&
        (countryFeatures.alwaysHasTimeOnJobSite || baseScenario.timeOnJobSite)
    }

    return enhancedScenario
  }, [baseScenario, countryFeatures])
}
